import { AgTechQueryBuilder, AgTechQueryProp, AgTechQueryResult, AgTechQueryRun } from "@agtech/core/data/queries/AgTechDataQueries"
import { AgTechNoRecordsPageProps } from "../../Pages/Error/AgTechNoRecordsPage"
import { AgTechGridHeaderConfiguration } from "../AgTechGridHeader"
import { AgTechGridEditConfiguration } from "../Features/AgTechGridEditing"
import { AgTechGridGroupingConfiguration } from "../Features/AgTechGridGrouping"
import { AgTechGridSearchProps } from "../Features/AgTechGridSearching"
import { AgTechGridSelectionConfiguraiton } from "../Features/AgTechGridSelection"
import { AgTechGridTabConfiguration } from "../Features/AgTechGridTabbing"
import { AgTechGridViewConfig } from "../Features/AgTechGridViews"
import { IAgTechGridItem, AgTechGridColumnMetadata } from "./AgTechGridData"
import { AgTechGridEntity } from "./AgTechGridEntities"

export type AgTechGridProps<TItem extends IAgTechGridItem, TContextData = {}> = AgTechGridConfiguration<TItem> & {
    data: {
        itemQuery: {
            queryResult: AgTechQueryResult<{ items: TItem[], context?: TContextData }>,
            refresh: () => Promise<any>
        }
    },
    behavior?: {
        onGridDataLoaded?: (data: TItem[]) => Promise<void>
    },
    columns: AgTechGridColumnMetadata<TItem, TContextData>[],
    header?: AgTechGridHeaderConfiguration,
    isRowEntityTheSame: (prev: TItem, curr: TItem) => boolean,
    propModifier?: AgTechGridPropsModifier<TItem, TContextData>
}

export const gridItems = <TItem extends IAgTechGridItem, TContextData = {}>(
    items: TItem[],
    context?: TContextData
) => {
    return AgTechQueryBuilder.buildQueryResult({
        data: {
            items,
            context
        }
    })
}

export type AgTechGridPropsModifier<TItem extends IAgTechGridItem, TContextData = {}> = 
    (props: AgTechGridProps<TItem, TContextData>) => AgTechGridProps<TItem, TContextData>

export type AgTechGridConfiguration<TItem extends IAgTechGridItem> = {
    name?: string,
    searchConfig?: AgTechGridSearchProps<TItem>,
    focusConfig?: AgTechGridFocusConfiguration<TItem>,
    selectionConfig?: AgTechGridSelectionConfiguraiton<TItem>,
    editConfig?: AgTechGridEditConfiguration<TItem>,
    groupingConfig?: AgTechGridGroupingConfiguration<TItem, string>,
    noRecordsConfig?: AgTechNoRecordsPageProps,
    tabConfig?: AgTechGridTabConfiguration<TItem>,
    actions?: AgTechGridActionsConfiguration<TItem>,
    viewConfig?: AgTechGridViewConfig<TItem>,
    sortingConfig?: AgTechGridSortingConfig<TItem>,
    sizingConfig?: AgTechGridSizingConfig
}

export type AgTechGridEventHandlers<TItem extends IAgTechGridItem> = {
    onItemSelected: (entity: AgTechGridEntity<TItem>, formEntity?: any) => Promise<void>,
    onItemDeselected: (entity: AgTechGridEntity<TItem>, formEntity?: any) => Promise<void>,
    onRowBlurred: (entity: AgTechGridEntity<TItem>, originalItem: TItem) => Promise<void>,
    onRowFocused: (focusedEntity: AgTechGridEntity<TItem>) => Promise<void>,
    onItemDeleted: (deletedEntity: AgTechGridEntity<TItem>) => Promise<void>,
    onEnterKeyPressed: (key: string, entity: AgTechGridEntity<TItem>, originalItem: TItem) => Promise<void>
}

/// Actions

export type AgTechGridActionsConfiguration<TItem> = {
    rendering?: (item: TItem) => React.ReactNode,
    width?: string,
    gridActions?: {
        rendering: () => React.ReactNode,
        width?: string
    }
};

/// Focus

export type AgTechGridFocusConfiguration<TItem extends IAgTechGridItem> = {
    onItemFocused: (item: TItem) => Promise<void>,
    focusedItem?: TItem,
    focusFirstItem?: boolean,
    highlightFocusedItem?: boolean,
}

/// Controls

export type AgTechGridControlProps<TValue> = {
    value: TValue,
    readonly?: boolean,
    onUpdated?: (value: TValue) => void
}

/// Sorting

export type AgTechGridSortingConfig<TItem extends IAgTechGridItem> = {
    getSortingKey: (item: TItem) => string | number
}

/// Sizing

export type AgTechGridSizingConfig = {
    size?: 'small' | 'standard',
    minRowsShown?: number
}